// src/lib/state/health.state.svelte.ts
// ====================================================================
// 3-LAYER HEALTH STATE — Phase 6b
// ====================================================================
//
// Preserves per-component health from the backend's /api/v1/health/
// response instead of collapsing everything into a single `isHealthy`
// boolean. The backend already returns:
//
//   {
//     database_connections: { frontend: bool, external: bool },
//     cache_status: { connected: bool, healthy: bool, ... },
//     opa_status: { healthy: bool, ... },
//     websocket_status: { ... },
//     environment: { ... }
//   }
//
// Phase 1-5 health handling collapsed this to `state.isHealthy` which
// was true only if both DBs were up. The UI had no way to tell the user
// "Database: offline, API: healthy, Frontend: healthy" — the exact
// 3-layer attribution the user has been asking for since the audit.
//
// Phase 6b keeps the full response and exposes:
//
//   - frontendHealth: always 'healthy' as long as this code is running
//   - apiHealth:      true if the health endpoint responded at all
//   - databaseHealth: composite of external + frontend DB statuses
//   - cacheHealth:    valkey status
//   - opaHealth:      OPA status
//   - websocketHealth: wired separately from connectionState
//
// Each layer is a `LayerStatus` object with `status`, `detail`, and
// `lastCheckedAt`. The LayerHealthBadge component reads these to
// render four pills in the header. The recovery state machine taxonomy
// is also extended with DATABASE_DOWN / CACHE_UNAVAILABLE / OPA_DOWN
// so banners can be layer-specific.
// ====================================================================

import { SvelteDate } from 'svelte/reactivity';
import type { HealthResponse } from '$lib/types/api/responses.types';

export type LayerStatusValue = 'healthy' | 'degraded' | 'down' | 'unknown';

export interface LayerStatus {
	status: LayerStatusValue;
	/** Human-readable detail for tooltips and banners. */
	detail: string;
	/** When we last received a status update for this layer. */
	lastCheckedAt: Date | null;
}

export interface HealthStoreState {
	// Raw response from the backend — preserved so admin views can
	// drill into per-component details without re-fetching.
	healthStatus: HealthResponse | null;

	// Environment flags (unchanged from earlier phases).
	isDevelopment: boolean;
	isProduction: boolean;
	microsoftSsoEnabled: boolean;
	authEnabled: boolean;

	// Composite flag kept for backward compat with code that still reads
	// healthState.isHealthy. New code should use the per-layer fields.
	isHealthy: boolean;

	// Per-layer status. The UI reads these directly.
	frontend: LayerStatus;   // this tab — always 'healthy' while alive
	api: LayerStatus;        // the FastAPI backend health endpoint
	database: LayerStatus;   // composite of frontend + external DB
	cache: LayerStatus;      // Valkey / Redis
	opa: LayerStatus;        // Open Policy Agent
	websocket: LayerStatus;  // WebSocket transport (mirrors connectionState)

	lastError: Error | null;
	lastChecked: Date | null;
}

const initialLayer: LayerStatus = {
	status: 'unknown',
	detail: 'Waiting for first health check',
	lastCheckedAt: null
};

const initialState: HealthStoreState = {
	healthStatus: null,
	isDevelopment: false,
	isProduction: false,
	microsoftSsoEnabled: false,
	authEnabled: true,
	isHealthy: false,

	// Frontend starts healthy because this code is running.
	// Other layers start 'unknown' — they'll be populated by the first
	// /health poll and by notifyRecoveryWs* signals. WebSocket starts
	// 'unknown' so the badge shows waiting gray, not a false "healthy"
	// green before the connection is actually established.
	frontend: { status: 'healthy', detail: 'Running', lastCheckedAt: null },
	api: { ...initialLayer },
	database: { ...initialLayer },
	cache: { ...initialLayer },
	opa: { ...initialLayer },
	websocket: { ...initialLayer },

	lastError: null,
	lastChecked: null
};

// Wrapped in an object because Svelte 5 requires reassignable state to
// be exported via an object, not a direct `let` binding.
export const healthState = $state<HealthStoreState>({ ...initialState });

// --------------------------------------------------------------------
// Mutators
// --------------------------------------------------------------------

export function reset(): void {
	Object.assign(healthState, initialState);
}

/**
 * Set the raw health response from the backend.
 * This is the ONE place per-layer status is computed from the wire
 * format. Everything downstream reads the derived LayerStatus fields.
 *
 * P10 fixes (from codex audit):
 *
 * - BLOCKER: cache field mismatch. Backend returns `cache_status.live_ping`
 *   as the health indicator. Previously we only read `cache_status.connected`
 *   which is never set, so every poll incorrectly marked cache as DOWN
 *   and the overall badge flipped red on healthy systems. Now we accept
 *   live_ping === true OR connected === true OR healthy === true OR
 *   the top-level cache_connected flag. Whichever one the backend chose,
 *   we treat it as healthy.
 *
 * - HIGH: frontend layer was being reset to 'healthy' on every poll,
 *   overwriting any 'down' state set by ErrorBoundary or +error.svelte.
 *   The fix: only set frontend to 'healthy' if it wasn't already 'down'.
 *   Errors detected at render time must persist until the user retries
 *   or navigates away (both of which explicitly restore it).
 */
export function setHealthStatus(status: HealthResponse): void {
	const now = new SvelteDate();
	healthState.healthStatus = status;
	healthState.lastChecked = now;

	// ---- Frontend (this tab) ----
	// P10 fix: preserve a 'down' state set by ErrorBoundary / +error.svelte.
	// Only refresh the timestamp + detail when we're already healthy or
	// coming from 'unknown'. A poll-driven reset would hide real render
	// errors the moment the next /health tick lands.
	if (healthState.frontend.status !== 'down') {
		healthState.frontend = {
			status: 'healthy',
			detail: 'Running',
			lastCheckedAt: now
		};
	}

	// ---- API ----
	// P10.2 fix: if we got a parseable body, the API is by definition
	// reachable — the HTTP handler ran and produced valid JSON. The
	// top-level `status: 'unhealthy'` flag the backend sets when a
	// component is down does NOT mean the API itself is down; it means
	// the API is reporting that some downstream component (DB/cache/OPA)
	// is broken. That attribution belongs in those layer pills, NOT
	// in the API pill. Previously we flipped api='down' on any
	// top-level 'unhealthy' flag, which meant a degraded database
	// incorrectly showed as "API down" in the badge.
	//
	// The API layer only goes 'down' when setError() is called (no body
	// parse — network error, CORS, DNS, etc.).
	healthState.api = {
		status: 'healthy',
		detail: 'Reachable',
		lastCheckedAt: now
	};

	// ---- Database (composite of frontend + external) ----
	const dbConnections = status.database_connections;
	const frontendDbOk = dbConnections?.frontend === true;
	const externalDbOk = dbConnections?.external === true;
	let dbStatus: LayerStatusValue;
	let dbDetail: string;
	if (frontendDbOk && externalDbOk) {
		dbStatus = 'healthy';
		dbDetail = 'Frontend + external databases online';
	} else if (frontendDbOk && !externalDbOk) {
		dbStatus = 'degraded';
		dbDetail = 'External database (Dynamics) unavailable';
	} else if (!frontendDbOk && externalDbOk) {
		dbStatus = 'degraded';
		dbDetail = 'Frontend database unavailable';
	} else {
		dbStatus = 'down';
		dbDetail = 'Both databases unavailable';
	}
	healthState.database = { status: dbStatus, detail: dbDetail, lastCheckedAt: now };

	// ---- Cache ----
	// P10 BLOCKER fix: accept any of the three indicators the backend
	// may set. The actual field is `cache_status.live_ping` (new) with
	// legacy fallbacks for `connected`, `healthy`, or top-level
	// cache_connected. Previously we only read `.connected` which is
	// never set, so the badge always flipped red.
	const cacheRaw = (status as { cache_status?: Record<string, unknown> }).cache_status;
	const cacheHealthy =
		cacheRaw?.live_ping === true ||
		cacheRaw?.connected === true ||
		cacheRaw?.healthy === true ||
		(status as { cache_connected?: boolean }).cache_connected === true;
	healthState.cache = {
		status: cacheHealthy ? 'healthy' : 'down',
		detail: cacheHealthy ? 'Valkey online' : 'Valkey unavailable',
		lastCheckedAt: now
	};

	// ---- OPA ----
	const opaRaw = (status as { opa_status?: Record<string, unknown> }).opa_status;
	const opaHealthy =
		opaRaw?.healthy === true || (status as { opa_connected?: boolean }).opa_connected === true;
	healthState.opa = {
		status: opaHealthy ? 'healthy' : 'down',
		detail: opaHealthy ? 'Policy engine online' : 'OPA unavailable',
		lastCheckedAt: now
	};

	// Backward-compat composite boolean. API is reachable by definition
	// here (we have a parseable body), so the composite only cares about
	// the downstream components.
	healthState.isHealthy = dbStatus === 'healthy' && cacheHealthy && opaHealthy;
}

export function setEnvironmentInfo(
	isHealthy: boolean,
	isDevelopment: boolean,
	microsoftSsoEnabled: boolean = false,
	authEnabled: boolean = true
): void {
	healthState.isHealthy = isHealthy;
	healthState.isDevelopment = isDevelopment;
	healthState.isProduction = !isDevelopment;
	healthState.microsoftSsoEnabled = microsoftSsoEnabled;
	healthState.authEnabled = authEnabled;
	healthState.lastChecked = new SvelteDate();
}

export function setError(error: Error): void {
	healthState.lastError = error;
	healthState.lastChecked = new SvelteDate();
	// When the health endpoint itself errors out, the API layer is down.
	healthState.api = {
		status: 'down',
		detail: `Health check failed: ${error.message}`,
		lastCheckedAt: new SvelteDate()
	};
}

/**
 * Direct per-layer mutator for targeted updates that don't come through
 * a full /health response. Example: the WebSocket transport drops out,
 * so we flip `websocket` to 'down' without waiting for the next health
 * poll cycle.
 */
export function setLayerStatus(
	layer: 'frontend' | 'api' | 'database' | 'cache' | 'opa' | 'websocket',
	status: LayerStatusValue,
	detail: string
): void {
	healthState[layer] = {
		status,
		detail,
		lastCheckedAt: new SvelteDate()
	};
}

// Snapshot helper for reading current state (used by debug tools).
export function getSnapshot(): HealthStoreState {
	return { ...healthState };
}
